// Strong/weak outcome SWOT view. Prefix: qbe-
// Glass migration guided by the design ReportsScreen SWOT quadrants (rp-q):
// four tone-coded frosted panels (support=danger, more-work=warning,
// appreciated=info, high-potential=success), translucent achievement cards,
// and centered empty states. Markup is unchanged — styling only.
export const outcomeGraphStrongWeakStyles = `
  .qbe-toggle-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    column-gap: 1rem;
    row-gap: 0.5rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
  }
  .qbe-toggle-group {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 1rem;
    row-gap: 0.5rem;
    flex-wrap: wrap;
  }
  .qbe-toggle-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 0.5rem;
    cursor: pointer;
  }
  .qbe-detail-toggle {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 0.5rem;
    cursor: pointer;
  }
  .qbe-chart-wrapper {
    width: 75%;
    margin: auto;
    margin-bottom: 2rem;
  }
  .qbe-chart-title {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .qbe-chart-title-text {
    font-weight: 700;
    font-size: var(--fs-lg);
    color: var(--text-primary);
  }
  .qbe-chart-card {
    border-radius: var(--rad-catchup-large);
    border: 1px solid var(--glass-flat-border);
    background: var(--glass-flat-solid);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    padding: var(--space-5);
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
  }
  .qbe-section-row {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
  @media (min-width: 1024px) {
    .qbe-section-row {
      flex-direction: row;
    }
  }

  /* Quadrant panel — frosted, tone-coded left border (design rp-q) */
  .qbe-panel {
    flex: 1 1 0%;
    position: relative;
    border-left: 5px solid;
    border-radius: var(--rad-catchup-large);
    padding: var(--space-6);
    /* No live backdrop-filter: this panel is an ancestor of .qbe-panel-scroll
       (overflow-y: auto) AND sits on the scrolling reports page — a blur here
       re-rasterizes every scroll frame. The tone-coded frost is carried by the
       modifier tints layered over --glass-flat-solid below (opaque, no blur). */
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    box-shadow:
      inset 1.5px 2.5px 0 -2px rgba(255, 255, 255, 0.85),
      inset -2px -2px 0 -2px rgba(255, 255, 255, 0.55),
      inset 0 0 0 1px rgba(255, 255, 255, 0.12),
      inset -0.5px -1px 6px 0 rgba(15, 72, 89, 0.05),
      0 10px 30px -14px rgba(15, 72, 89, 0.18);
  }
  /* Tone tint layered over the opaque solid (was a translucent tint that
     relied on .qbe-panel's now-removed blur to read as frosted glass). */
  .qbe-panel-support {
    background: linear-gradient(rgba(226, 91, 91, 0.10), rgba(226, 91, 91, 0.10)), var(--glass-flat-solid);
    border-left-color: var(--color-error);
  }
  .qbe-panel-more-work {
    background: linear-gradient(rgba(245, 166, 35, 0.08), rgba(245, 166, 35, 0.08)), var(--glass-flat-solid);
    border-left-color: var(--accent-gold);
  }
  .qbe-panel-appreciated {
    background: linear-gradient(rgba(84, 187, 203, 0.10), rgba(84, 187, 203, 0.10)), var(--glass-flat-solid);
    border-left-color: var(--brand);
  }
  .qbe-panel-high-potential {
    background: linear-gradient(rgba(172, 215, 71, 0.10), rgba(172, 215, 71, 0.10)), var(--glass-flat-solid);
    border-left-color: var(--accent-lime);
  }
  .qbe-panel-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: var(--space-5);
    column-gap: var(--space-3);
  }
  .qbe-panel-heading {
    font-size: var(--fs-lg);
    font-weight: 700;
    color: var(--brand-darker);
    line-height: 1.2;
  }
  .qbe-panel-subheading {
    font-size: var(--fs-md);
    color: var(--text-muted);
    margin-top: 3px;
  }
  .qbe-panel-scroll {
    max-height: 400px;
    overflow-y: auto;
  }
  .qbe-card-list > * + * {
    margin-top: var(--space-3);
  }

  /* Achievement card — translucent white on the tinted panel (design rp-ach) */
  .qbe-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: var(--rad-catchup-medium);
    padding: var(--space-4) var(--space-5);
    border: none;
    border-left: 3px solid transparent;
    cursor: pointer;
    transition: transform var(--duration-base) var(--ease-standard),
      box-shadow var(--duration-base) var(--ease-standard);
  }
  .qbe-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px -10px rgba(15, 72, 89, 0.25);
  }
  .qbe-card-support {
    border-left-color: var(--color-error);
  }
  .qbe-card-more-work {
    border-left-color: var(--accent-gold);
  }
  .qbe-card-appreciated {
    border-left-color: var(--brand);
  }
  .qbe-card-high-potential {
    border-left-color: var(--accent-lime);
  }
  .qbe-card-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
  }
  .qbe-card-main {
    flex: 1 1 0%;
    min-width: 0;
  }
  .qbe-card-code {
    font-weight: 700;
    font-size: var(--fs-lg);
    color: var(--text-primary);
    margin-bottom: 2px;
  }
  .qbe-card-name {
    font-size: var(--fs-md);
    color: var(--text-secondary);
    line-height: 1.45;
  }
  .qbe-card-stats {
    margin-left: var(--space-3);
    text-align: right;
    white-space: nowrap;
    color: var(--text-muted);
    line-height: 1.5;
  }
  .qbe-stat-line {
    font-size: var(--fs-md);
    margin-bottom: 0.25rem;
  }
  .qbe-stat-line-last {
    font-size: var(--fs-md);
  }
  .qbe-stat-value {
    font-weight: 700;
    color: var(--text-primary);
  }

  /* Empty quadrant now uses the shared library <EmptyState compact/> — see
     src/library/components/glass/EmptyState. (The old .qbe-empty* rules were
     removed with that migration.) The quadrant panels are small, so shrink the
     shared empty state here only (scoped to .qbe-panel-scroll — the shared
     component is untouched elsewhere). */
  .qbe-panel-scroll .es-root {
    padding: var(--space-4) var(--space-3);
    gap: var(--space-2);
  }
  .qbe-panel-scroll .es-medallion {
    /* --es-halo scales with the disc so the medallion keeps its proportions */
    --es-halo: 4px;
    width: 40px;
    height: 40px;
    font-size: 18px;
    margin-bottom: 0;
  }
  .qbe-panel-scroll .es-title {
    font-size: var(--fs-base);
  }

  /* Scatter (Detay gör) — bespoke SVG, design rp-scatter ported to qbe- */
  .qbe-scatter {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    border-radius: var(--rad-catchup-large);
    border: 1px solid var(--glass-flat-border);
    background: var(--glass-flat-solid);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
    padding: var(--space-5);
  }
  .qbe-scatter-title {
    font-size: var(--fs-2xl);
    font-weight: 700;
    color: var(--text-primary);
  }
  .qbe-scatter-legend {
    display: flex;
    gap: var(--space-5);
    justify-content: center;
    flex-wrap: wrap;
  }
  .qbe-scatter-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-md);
    color: var(--text-secondary);
    font-weight: 600;
  }
  .qbe-scatter-legend-item i {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .qbe-scatter-legend-line {
    width: 18px !important;
    height: 4px !important;
    border-radius: 99px !important;
  }
  .qbe-scatter-plot { position: relative; }
  .qbe-scatter-svg { width: 100%; height: auto; }
  .qbe-scatter-tip {
    position: absolute;
    top: var(--space-4);
    right: var(--space-4);
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--rad-catchup-medium);
    background: var(--glass-popover-bg-strong);
    -webkit-backdrop-filter: var(--glass-popover-filter);
    backdrop-filter: var(--glass-popover-filter);
    border: 1px solid var(--glass-border);
    box-shadow: var(--glass-popover-shadow);
    pointer-events: none;
  }
  .qbe-scatter-tip b { color: var(--text-primary); font-size: var(--fs-base); }
  .qbe-scatter-tip span { font-size: var(--fs-md); color: var(--text-secondary); }
  .qbe-scatter-tipstat { color: var(--text-muted) !important; }
  .qbe-scatter-tipstat b { color: var(--brand-dark); }
`;
